import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

import {
  computeCanonicalTreeTopology,
  type CanonicalTreeTopology,
} from './CanonicalTreeTopologyAuthority';

import {
  projectCanonicalTreeForViewer,
  type CanonicalTreeViewerProjectionResult,
  type CanonicalViewerBranch,
} from './CanonicalTreeViewerProjection';

import type {
  CanonicalTreeRelationshipEdge,
} from './CanonicalTreeRelationshipEdge';

export type CanonicalTreeProjectionParityClassification =
  | 'PARITY'
  | 'CANONICAL-ADDITION'
  | 'LEGACY-ONLY'
  | 'GENERATION-MISMATCH'
  | 'BRANCH-MISMATCH'
  | 'UNRESOLVED';

export interface CanonicalTreeProjectionParityMemberResult {
  readonly memberId: string;
  readonly classification:
    CanonicalTreeProjectionParityClassification;

  readonly legacyGeneration:
    number | null;

  readonly canonicalGeneration:
    number | null;

  readonly legacyBranch:
    string | null;

  readonly canonicalBranch:
    CanonicalViewerBranch | null;

  readonly details:
    readonly string[];
}

export interface CanonicalTreeProjectionParitySummary {
  readonly parityCount: number;
  readonly canonicalAdditionCount: number;
  readonly legacyOnlyCount: number;
  readonly generationMismatchCount: number;
  readonly branchMismatchCount: number;
  readonly unresolvedCount: number;
}

export type CanonicalTreeProjectionMigrationReadiness =
  | 'READY'
  | 'READY-WITH-CANONICAL-ADDITIONS'
  | 'BLOCKED';

export interface CanonicalTreeProjectionParityGateResult {
  readonly viewerId: string;

  readonly legacy:
    CanonicalTreeTopology;

  readonly canonical:
    CanonicalTreeViewerProjectionResult;

  readonly members:
    readonly CanonicalTreeProjectionParityMemberResult[];

  readonly summary:
    CanonicalTreeProjectionParitySummary;

  readonly migrationReadiness:
    CanonicalTreeProjectionMigrationReadiness;
}

export interface CanonicalTreeProjectionParityGateInput {
  readonly viewerId: string;
  readonly members:
    readonly GraphMember[];
  readonly edges:
    readonly CanonicalTreeRelationshipEdge[];
}

const legacyBranchClass = (
  path: string | null,
): CanonicalViewerBranch | null => {
  if (!path) return null;

  if (path === 'self') {
    return 'self';
  }

  if (
    path === 'ancestor.0' ||
    path.startsWith('ancestor.0.')
  ) {
    return 'maternal';
  }

  if (
    path === 'ancestor.1' ||
    path.startsWith('ancestor.1.')
  ) {
    return 'paternal';
  }

  if (path.includes('.child.')) {
    return 'descendant';
  }

  if (path.endsWith('.partner')) {
    return 'partner-affiliated';
  }

  return 'unresolved';
};

const freezeMemberResult = (
  result:
    CanonicalTreeProjectionParityMemberResult,
): CanonicalTreeProjectionParityMemberResult =>
  Object.freeze({
    ...result,
    details:
      Object.freeze([...result.details]),
  });

const countByClassification = (
  members:
    readonly CanonicalTreeProjectionParityMemberResult[],
  classification:
    CanonicalTreeProjectionParityClassification,
): number =>
  members.filter(
    member =>
      member.classification ===
      classification,
  ).length;

const classifyMemberParity = (
  memberId: string,
  legacy: CanonicalTreeTopology,
  canonical:
    CanonicalTreeViewerProjectionResult,
): CanonicalTreeProjectionParityMemberResult => {
  const details: string[] = [];

  const legacyGeneration =
    legacy.generationByMemberId[memberId] ??
    null;

  const canonicalProjection =
    canonical.projections.get(memberId) ??
    null;

  const canonicalGeneration =
    canonicalProjection?.generation ??
    null;

  const legacyBranchPath =
    legacy.branchPathByMemberId[memberId] ??
    null;

  const legacyBranch =
    legacyBranchClass(
      legacyBranchPath,
    );

  const canonicalBranch =
    canonicalProjection?.branch ??
    null;

  const canonicalUnresolved =
    canonical.unresolvedEvidence.some(
      evidence =>
        evidence.memberIds.includes(
          memberId,
        ),
    );

  if (canonicalUnresolved) {
    details.push(
      'Canonical projection reported unresolved evidence for this member.',
    );

    return freezeMemberResult({
      memberId,
      classification:
        'UNRESOLVED',
      legacyGeneration,
      canonicalGeneration,
      legacyBranch:
        legacyBranchPath,
      canonicalBranch,
      details,
    });
  }

  const legacyReachable =
    legacyGeneration !== null ||
    legacyBranchPath !== null;

  const canonicalReachable =
    canonicalProjection !== null;

  if (
    canonicalReachable &&
    !legacyReachable
  ) {
    details.push(
      'Member is reachable only through canonical relationship authority.',
    );

    return freezeMemberResult({
      memberId,
      classification:
        'CANONICAL-ADDITION',
      legacyGeneration,
      canonicalGeneration,
      legacyBranch:
        legacyBranchPath,
      canonicalBranch,
      details,
    });
  }

  if (
    legacyReachable &&
    !canonicalReachable
  ) {
    details.push(
      'Member is reachable only through legacy topology authority.',
    );

    return freezeMemberResult({
      memberId,
      classification:
        'LEGACY-ONLY',
      legacyGeneration,
      canonicalGeneration,
      legacyBranch:
        legacyBranchPath,
      canonicalBranch,
      details,
    });
  }

  if (
    !legacyReachable &&
    !canonicalReachable
  ) {
    details.push(
      'Member is unreachable in both topology authorities.',
    );

    return freezeMemberResult({
      memberId,
      classification:
        'PARITY',
      legacyGeneration,
      canonicalGeneration,
      legacyBranch:
        legacyBranchPath,
      canonicalBranch,
      details,
    });
  }

  if (
    legacyGeneration !==
    canonicalGeneration
  ) {
    details.push(
      `Legacy generation ${String(
        legacyGeneration,
      )} differs from canonical generation ${String(
        canonicalGeneration,
      )}.`,
    );

    return freezeMemberResult({
      memberId,
      classification:
        'GENERATION-MISMATCH',
      legacyGeneration,
      canonicalGeneration,
      legacyBranch:
        legacyBranchPath,
      canonicalBranch,
      details,
    });
  }

  if (
    legacyBranch !==
    canonicalBranch
  ) {
    details.push(
      `Legacy branch ${String(
        legacyBranch,
      )} from path ${String(
        legacyBranchPath,
      )} differs from canonical branch ${String(
        canonicalBranch,
      )}.`,
    );

    return freezeMemberResult({
      memberId,
      classification:
        'BRANCH-MISMATCH',
      legacyGeneration,
      canonicalGeneration,
      legacyBranch:
        legacyBranchPath,
      canonicalBranch,
      details,
    });
  }

  details.push(
    'Legacy and canonical topology agree.',
  );

  return freezeMemberResult({
    memberId,
    classification:
      'PARITY',
    legacyGeneration,
    canonicalGeneration,
    legacyBranch:
      legacyBranchPath,
    canonicalBranch,
    details,
  });
};

export const runCanonicalTreeProjectionParityGate = (
  input: CanonicalTreeProjectionParityGateInput,
): CanonicalTreeProjectionParityGateResult => {
  const members =
    [...input.members];

  const legacy =
    computeCanonicalTreeTopology(
      members,
      input.viewerId,
    );

  const canonical =
    projectCanonicalTreeForViewer({
      viewerId:
        input.viewerId,
      members,
      edges:
        input.edges,
    });

  const memberResults =
    members
      .map(
        member =>
          classifyMemberParity(
            member.id,
            legacy,
            canonical,
          ),
      )
      .sort(
        (left, right) =>
          left.memberId.localeCompare(
            right.memberId,
          ),
      );

  const summary:
    CanonicalTreeProjectionParitySummary =
      Object.freeze({
        parityCount:
          countByClassification(
            memberResults,
            'PARITY',
          ),

        canonicalAdditionCount:
          countByClassification(
            memberResults,
            'CANONICAL-ADDITION',
          ),

        legacyOnlyCount:
          countByClassification(
            memberResults,
            'LEGACY-ONLY',
          ),

        generationMismatchCount:
          countByClassification(
            memberResults,
            'GENERATION-MISMATCH',
          ),

        branchMismatchCount:
          countByClassification(
            memberResults,
            'BRANCH-MISMATCH',
          ),

        unresolvedCount:
          countByClassification(
            memberResults,
            'UNRESOLVED',
          ),
      });

  const blocked =
    summary.legacyOnlyCount > 0 ||
    summary.generationMismatchCount > 0 ||
    summary.branchMismatchCount > 0 ||
    summary.unresolvedCount > 0 ||
    canonical.unresolvedEvidence.length > 0 ||
    canonical.conflictingEvidence.length > 0;

  const migrationReadiness:
    CanonicalTreeProjectionMigrationReadiness =
      blocked
        ? 'BLOCKED'
        : summary.canonicalAdditionCount > 0
          ? 'READY-WITH-CANONICAL-ADDITIONS'
          : 'READY';

  return Object.freeze({
    viewerId:
      input.viewerId,

    legacy:
      Object.freeze({
        generationByMemberId:
          Object.freeze({
            ...legacy.generationByMemberId,
          }),

        branchPathByMemberId:
          Object.freeze({
            ...legacy.branchPathByMemberId,
          }),
      }),

    canonical,

    members:
      Object.freeze(
        memberResults,
      ),

    summary,

    migrationReadiness,
  });
};
